import { useQuery } from "@tanstack/react-query";
import { ClientResponseError } from "pocketbase";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Spinner } from "@/components/controls/spinner";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics/analytics";
import { useAuth } from "@/lib/auth/auth-context";
import { LEGAL_URLS } from "@/lib/auth/legal";
import { pb } from "@/lib/core/pocketbase";

/** The providers the login page offers, in this order, when the server has them configured. */
const OFFERED = ["google", "apple", "microsoft"] as const;
type Provider = (typeof OFFERED)[number];

/** Monochrome marks (currentColor) so they follow the theme. */
const ICONS: Record<Provider, React.ReactNode> = {
  google: (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4" fill="currentColor">
      <path d="M21.35 11.1H12v2.98h5.35c-.23 1.4-1.64 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.96S8.78 6.26 12 6.26c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.68 3.71 14.54 2.8 12 2.8 6.92 2.8 2.8 6.92 2.8 12s4.12 9.2 9.2 9.2c5.31 0 8.83-3.73 8.83-8.99 0-.6-.07-1.06-.15-1.51z" />
    </svg>
  ),
  apple: (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4" fill="currentColor">
      <path d="M16.37 12.7c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.74-.78-2.87-.76-1.47.02-2.83.86-3.59 2.17-1.54 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.24 2.73 2.2 1.1-.05 1.51-.7 2.84-.7 1.32 0 1.7.7 2.86.68 1.18-.02 1.93-1.07 2.65-2.13.84-1.22 1.18-2.41 1.2-2.47-.03-.01-2.27-.87-2.29-3.46zM14.2 6.25c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.69-.92 2.68.97.08 1.96-.49 2.56-1.23z" />
    </svg>
  ),
  microsoft: (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4" fill="currentColor">
      <path d="M3 3h8.5v8.5H3zM12.5 3H21v8.5h-8.5zM3 12.5h8.5V21H3zM12.5 12.5H21V21h-8.5z" />
    </svg>
  ),
};

/**
 * "Continue with Google / Apple / Microsoft" above the login and sign-up forms, for the
 * providers configured on the server (none in dev and e2e unless OAUTH_* is set).
 */
export function OAuthButtons({ signUp = false }: { signUp?: boolean }) {
  const { t } = useTranslation();
  const { loginWithOAuth } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const methods = useQuery({
    queryKey: ["auth-methods"],
    queryFn: () => pb.collection("users").listAuthMethods(),
    staleTime: Infinity,
  });
  const providers = OFFERED.filter((name) =>
    methods.data?.oauth2.providers.some((provider) => provider.name === name),
  );
  if (!methods.data?.oauth2.enabled || providers.length === 0) return null;

  async function continueWith(provider: Provider) {
    setBusy(provider);
    try {
      await loginWithOAuth(provider);
      track(signUp ? "signup_oauth" : "login_oauth", { provider });
    } catch (error) {
      // Closing the popup cancels the sign-in: not an error worth a toast.
      if (!(error instanceof ClientResponseError && error.isAbort)) {
        toast.error(t("login.oauthFailed"));
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3" data-testid="oauth-buttons">
      {providers.map((provider) => (
        <Button
          key={provider}
          type="button"
          variant="outline"
          className="w-full"
          disabled={busy !== null}
          onClick={() => void continueWith(provider)}
          data-testid={`oauth-${provider}`}
        >
          {busy === provider ? <Spinner /> : ICONS[provider]}
          {t(`login.oauth.${provider}`)}
        </Button>
      ))}
      {signUp && (
        <p className="text-center text-xs text-muted-foreground">
          <Trans
            i18nKey="login.oauthTerms"
            components={{
              terms: (
                <a href={LEGAL_URLS.terms} target="_blank" rel="noreferrer" className="underline" />
              ),
              privacy: (
                <a
                  href={LEGAL_URLS.privacy}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                />
              ),
            }}
          />
        </p>
      )}
      <div className="flex items-center gap-3 text-xs text-muted-foreground uppercase">
        <span className="h-px flex-1 bg-border" /> {t("login.or")}{" "}
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}
